import { useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import companiesApi from 'apiService/companies'
import { StorefrontCompany } from '@local-types/user/storefrontUser'

const PAGE_SIZE = 20

// Server-side, debounced company search with pagination. Only fetches when `enabled`
// is true so it never hits the salesPerson-only endpoint for regular users.
export const useCompanySearch = (enabled: boolean) => {
	const [search, setSearch] = useState('')
	const [debouncedSearch] = useDebounce(search, 300)
	const [companies, setCompanies] = useState<StorefrontCompany[]>([])
	const [page, setPage] = useState(1)
	const [totalRecords, setTotalRecords] = useState(0)
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (!enabled) return
		let active = true
		setLoading(true)
		companiesApi
			.search(1, PAGE_SIZE, debouncedSearch)
			.then(res => {
				if (!active) return
				setCompanies(res.data.results)
				setTotalRecords(res.data.totalRecords)
				setPage(1)
			})
			.catch(() => {
				if (!active) return
				setCompanies([])
				setTotalRecords(0)
			})
			.finally(() => {
				if (active) setLoading(false)
			})
		return () => {
			active = false
		}
	}, [enabled, debouncedSearch])

	const loadMore = async () => {
		if (loading) return
		const nextPage = page + 1
		setLoading(true)
		try {
			const res = await companiesApi.search(nextPage, PAGE_SIZE, debouncedSearch)
			setCompanies(prev => [...prev, ...res.data.results])
			setTotalRecords(res.data.totalRecords)
			setPage(nextPage)
		} finally {
			setLoading(false)
		}
	}

	const hasMore = companies.length < totalRecords

	return { search, setSearch, companies, loading, loadMore, hasMore, totalRecords }
}

export default useCompanySearch
